// Types for the AutoCoder Throughput overview (VIS-T1).
// Mirrors goapi/pkg/server/autocoder_throughput.go — hand-written rather than
// generated because none of these shapes are persisted entities.

export type ThroughputWindowKey = '60m' | '6h' | '24h' | '7d'

/** Where a lane's TPM/RPM denominator came from. Read every saturation figure
 *  together with this: only `arm-seed` lanes are calibrated against real Azure
 *  deployment limits. */
export type LimitSource = 'arm-seed' | 'hand-tuned' | 'none'

export interface ThroughputWindowInfo {
  key: ThroughputWindowKey
  label: string
  start: string
  end: string
  /** Server-chosen bucket width. The client cannot request a finer one. */
  bucketSeconds: number
  bucketCount: number
  /** Server-chosen refresh cadence. Not purely a property of the window: it is
   *  cut to the fast cadence whenever batches are pending or running. */
  pollSeconds: number
  /** True when pollSeconds was cut below this window's resting cadence because
   *  work is in flight — the caption must say what is actually happening. */
  pollBoostedByActivity: boolean
}

/** The headline "is it moving right now" rate, measured over a fixed trailing
 *  span rather than the selected window's bucket. Reading the last complete
 *  bucket of a 7-day window reported 0/min for up to two hours mid-run. */
export interface ThroughputLiveRate {
  start: string
  end: string
  spanSeconds: number
  tokens: number
  requests: number
  tokensPerMin: number
  requestsPerMin: number
  /**
   * Most recent AutoCoder LLM call in scope, ignoring the selected window.
   * null means this scope has never made one. Every rate on this page is a
   * trailing measurement and therefore falls to 0 the instant work stops, so
   * this is the only field that separates "idle since 15:44" from "never ran".
   */
  lastActivityAt: string | null
}

export interface ThroughputBucket {
  t: string
  tokens: number
  requests: number
  tokensPerMin: number
  requestsPerMin: number
}

export interface ThroughputLane {
  laneId: string
  label: string
  /** false for the "unknown lane" buckets — shown, never dropped. */
  known: boolean
  /** The raw logged model strings folded into this lane. */
  rawModels: string[]
  totalTokens: number
  totalRequests: number
  peakTokensPerMin: number
  peakRequestsPerMin: number
  limitTpm: number
  limitRpm: number
  limitSource: LimitSource
  peakTpmPercent: number | null
  buckets: ThroughputBucket[]
}

export interface ThroughputQueueState {
  state: string
  count: number
  known: boolean
}

export interface ThroughputQueueLane {
  modelVersion: string
  laneId: string
  resolved: boolean
  total: number
  byState: Record<string, number>
}

/** Queue depth at one instant — reconstructed from batch lifecycle timestamps,
 *  never sampled. */
export interface ThroughputDepthPoint {
  t: string
  pending: number
  running: number
}

export interface ThroughputQueue {
  states: ThroughputQueueState[]
  byModelVersion: ThroughputQueueLane[]
  oldestPendingAt: string | null
  oldestPendingAgeSeconds: number | null
  /** One point per window bucket; the newest point is the depth right now. */
  depth: ThroughputDepthPoint[]
  depthNote: string
  laneVocabularyNote: string
}

/** Progress of the CURRENT RUN. Records lead (the unit an operator counts);
 *  batches are disclosed because they are the unit the ETA is measured in.
 *  Every number is a plain 0 when `active` is false. */
export interface ThroughputBacklog {
  active: boolean
  runStartedAt: string | null
  totalRecords: number
  codedRecords: number
  remainingRecords: number
  percentComplete: number
  totalBatches: number
  completedBatches: number
  runningBatches: number
  pendingBatches: number
  failedBatches: number
  /** Observed completion rate; 0 whenever no rate is claimed. */
  batchesPerMin: number
  /** null whenever the page refuses to guess — render "—", never a number. */
  etaSeconds: number | null
  /** Where the estimate came from, or why there isn't one. */
  etaBasis: string
}

export interface ThroughputCodingBucket {
  t: string
  batches: number
  records: number
}

export interface ThroughputPipeline {
  pipelineId: string
  name: string
  customer: string
  job: string
  batches: number
  records: number
}

export interface ThroughputCoding {
  buckets: ThroughputCodingBucket[]
  totalBatches: number
  totalRecords: number
  topPipelines: ThroughputPipeline[]
  source: string
}

/**
 * D4 — the DEFICIT: records that EXIST and are not in any live batch.
 *
 * Every queue number on this page counts ENQUEUED work, which is how a job
 * could hold 2,474 uncoded records for hours while the page said "queue empty".
 * The deficit is the complement: the reservoir behind the gate.
 */
export interface ThroughputDeficitPipeline {
  pipelineId: string
  name: string
  customer: string
  job: string
  /** Uncoded records (pending/failed/queued/running) that no live batch owns. */
  records: number
  /** The part of `records` nothing retries automatically: failed records. There
   *  is no sweep of any kind since T12/T19, so they wait for /retry-failed. */
  failedRecords: number
  /** Raw hint from the server; unknown values still render. */
  reason: string
  reasonLabel: string
  /** The DRAIN gate: with it shut, records that entered a pipeline wait in the
   *  lanes and nothing is pulled off. T19 (2026-08-12) retired the other gate
   *  this used to sit beside (`batchingOff` — the seal gate): entering a
   *  pipeline is now one atomic write, so there is no unbatched state left for
   *  a second gate to describe. */
  codingOff: boolean
  liveBatches: number
}

export interface ThroughputDeficit {
  records: number
  /** The part of the total that is deliberate back-pressure — coding is shut —
   *  rather than something stuck. The name predates the (now-retired) E2 gate
   *  split and is kept so the wire contract does not move. */
  heldByCodingOff: number
  /** The part of the total that is failed records: NOT held by the gate and NOT
   *  coming back on their own. Only an operator retry clears it, so it is
   *  counted apart from `heldByCodingOff`. */
  needsRetry: number
  pipelines: ThroughputDeficitPipeline[]
  note: string
}

/** Known reason hints, mirroring store.DeficitReason* in Go. */
export const DEFICIT_REASON_CODING_OFF = 'coding_off'
export const DEFICIT_REASON_NEVER_ENQUEUED = 'never_enqueued'
export const DEFICIT_REASON_PARTIALLY_ENQUEUED = 'partially_enqueued'
export const DEFICIT_REASON_NEEDS_RETRY = 'needs_retry'

/**
 * stalledDeficitPipelines — the pipelines that are genuinely stuck: they hold
 * uncoded records, coding is ON, and they have NO live batch at all.
 *
 * Per pipeline, deliberately. The customer-wide queue counts cannot answer this
 * question: one pipeline happily draining would otherwise silence the alarm for
 * a neighbour that has never enqueued anything.
 */
export const stalledDeficitPipelines = (
  deficit: ThroughputDeficit,
): ThroughputDeficitPipeline[] =>
  deficit.pipelines.filter((p) => p.records > 0 && !p.codingOff && p.liveBatches === 0)

/**
 * isDeficitStalled — the panel's single alarm predicate, shared by the sentence
 * and the border colour so the two can never disagree.
 *
 * Falls back to the global queue counts only when the payload carries no
 * per-pipeline rows (scoped views that omit the list); with rows present the
 * per-pipeline facts always win.
 */
export const isDeficitStalled = (
  deficit: ThroughputDeficit,
  pendingBatches: number,
  runningBatches: number,
): boolean => {
  if (deficit.records === 0) return false
  if (deficit.pipelines.length > 0) return stalledDeficitPipelines(deficit).length > 0
  return pendingBatches + runningBatches === 0 && deficit.heldByCodingOff < deficit.records
}

/**
 * The one sentence the panel leads with. The distinction that matters at a
 * glance is deficit-with-nothing-scheduled (nothing is going to happen) versus
 * deficit-while-draining (normal).
 */
export const describeDeficit = (
  deficit: ThroughputDeficit,
  pendingBatches: number,
  runningBatches: number,
): string => {
  if (deficit.records === 0) return 'Every record is coded or in the queue.'
  const queueEmpty = pendingBatches + runningBatches === 0
  const held = deficit.heldByCodingOff
  if (queueEmpty && held === deficit.records) {
    // Coding is the only gate left since T19 (2026-08-12) retired the seal
    // gate this sentence used to have to choose between.
    return `${formatExact(deficit.records)} records waiting behind a closed gate — coding is switched off, so nothing will be picked up.`
  }
  if (isDeficitStalled(deficit, pendingBatches, runningBatches)) {
    if (queueEmpty) {
      return `${formatExact(deficit.records)} records are uncoded and NOTHING is queued — the queue being empty does not mean the work is done.`
    }
    const stuck = stalledDeficitPipelines(deficit)
    const records = stuck.reduce((sum, p) => sum + p.records, 0)
    return `${formatExact(records)} records in ${stuck.length} pipeline${stuck.length === 1 ? '' : 's'} have no batches at all, while the rest of the queue drains.`
  }
  return `${formatExact(deficit.records)} records are uncoded and not yet queued, while the queue drains.`
}

/** Deficit colour: deliberately NOT the queue's orange/blue — the reservoir is
 *  a different thing from the queue, and must not read as more queue. */
export const DEFICIT_COLOR = '#8E44AD'

/**
 * The pipeline's LIFETIME record-state breakdown: every record ever sent to it,
 * counted once, in the state of its current coding.
 *
 * This is the one fact the Import Status section owned that nothing else could
 * tell — every other panel here counts batches, or counts the current run.
 *
 * There is deliberately no percentage. Import Status published one and it was
 * misleading for a long-lived pipeline: the denominator grows on every ingest,
 * so a "% done" slides BACKWARDS when new data lands. Counts stay true when the
 * denominator moves. For a progress bar, use `backlog` — it is scoped to the
 * current run, whose denominator is fixed for its lifetime.
 */
export interface ThroughputRecordStates {
  /** False on the customer-wide admin view, where these are not computed. Check
   *  this before rendering: six zeroes must not read as "an idle pipeline". */
  scoped: boolean
  total: number
  pending: number
  queued: number
  running: number
  completed: number
  failed: number
  note: string
}

export interface AutoCoderThroughputResponse {
  customer: string
  /** "" for the customer-wide admin view; the pipeline's id when every panel in
   *  this payload was filtered to one pipeline. Which slice of work is being
   *  described is a property of the DATA, never of the component rendering it. */
  pipelineId: string
  generatedAt: string
  window: ThroughputWindowInfo
  live: ThroughputLiveRate
  backlog: ThroughputBacklog
  lanes: ThroughputLane[]
  queue: ThroughputQueue
  coding: ThroughputCoding
  /** D4 — what is NOT in the queue. */
  deficit: ThroughputDeficit
  /** Lifetime records by state. Per-pipeline only — see `scoped`. */
  recordStates: ThroughputRecordStates
}

export const WINDOW_OPTIONS: { value: ThroughputWindowKey; label: string }[] = [
  { value: '60m', label: '60 min' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
]

export const LIMIT_SOURCE_LABELS: Record<LimitSource, string> = {
  'arm-seed': 'ARM-calibrated seed',
  'hand-tuned': 'Hand-tuned estimate',
  none: 'No published limit',
}

/** Compact number formatting for rates and totals (1.2M, 34.5k, 812). */
export const formatCompact = (n: number): string => {
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`
  if (abs >= 10) return n.toFixed(0)
  if (abs === 0) return '0'
  return n.toFixed(1)
}

/** Human duration for queue ages: 45s, 12m, 3h 20m, 2d 4h. */
export const formatAge = (seconds: number | null): string => {
  if (seconds === null || !Number.isFinite(seconds)) return '—'
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return h > 0 && m % 60 > 0 ? `${h}h ${m % 60}m` : `${h}h`
  const d = Math.floor(h / 24)
  return h % 24 > 0 ? `${d}d ${h % 24}h` : `${d}d`
}

/** Exact count with thousands separators. Records are the unit an operator
 *  counts, so the backlog panel shows them whole rather than as "1.8k". */
export const formatExact = (n: number): string =>
  Number.isFinite(n) ? Math.round(n).toLocaleString() : '—'

/** Refresh cadence for the page caption: 30s, 5m, 15m. */
export const formatCadence = (seconds: number): string =>
  seconds >= 60 ? `${Math.round(seconds / 60)}m` : `${Math.round(seconds)}s`

/**
 * Time remaining. An ETA is an ESTIMATE and is marked as one with "≈" — the
 * same convention the lane table uses for hand-tuned saturation. A null ETA
 * (fewer than three completed batches, or a stalled run) renders as "—"; the
 * page never guesses a number it has not earned.
 */
export const formatEta = (seconds: number | null): string =>
  seconds === null || !Number.isFinite(seconds) ? '—' : `≈${formatAge(seconds)}`

/** Queue-depth series colours, deliberately the same vocabulary as the queue
 *  state chips: pending is warning-orange, running is info-blue. */
export const DEPTH_COLORS = {
  pending: '#E8710A',
  running: '#2E7DF7',
} as const

/**
 * Saturation text. Hand-tuned denominators are prefixed with "≈" and never
 * presented as a measurement — the three #954 Foundry lanes carry 450k guesses
 * and the Anthropic lanes have no discovery path at all.
 */
export const formatSaturation = (
  percent: number | null,
  source: LimitSource,
): string => {
  if (percent === null || source === 'none') return '—'
  const value = `${percent < 1 && percent > 0 ? percent.toFixed(2) : percent.toFixed(1)}%`
  return source === 'hand-tuned' ? `≈${value}` : value
}
